import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, Layers, Rocket, Boxes, GitBranch, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { Overline, SectionHeading } from "@/components/Section";
import PlatformCard from "@/components/PlatformCard";

const SERVICES = [
  { icon: Layers, code: "PaaS", title: "Platform-as-a-Service", desc: "Full-stack platforms designed, shipped, and operated end-to-end on production-grade infrastructure." },
  { icon: Boxes, code: "SaaS", title: "Software-as-a-Service", desc: "Subscription products with billing, auth, and analytics wired from day one — built to earn." },
  { icon: GitBranch, code: "RaaS", title: "Revenue-as-a-Service", desc: "We don't just build — we own and operate. Platforms run as their own economic engines." },
];

const STATS = [
  { value: "1", label: "Platform LIVE" },
  { value: "3", label: "Build models" },
  { value: "∞", label: "Slots for clients" },
  { value: "100%", label: "TIB-operated" },
];

export default function Home() {
  const [platforms, setPlatforms] = useState([]);
  const [live, setLive] = useState(null);

  useEffect(() => {
    api.get("/public/platforms.json").then((r) => setPlatforms(r.data.internal || [])).catch(() => {});
    api.get("/public/ideadepot-live").then((r) => setLive(r.data.card)).catch(() => {});
  }, []);

  const liveList = platforms.filter((p) => p.status === "live");
  const soonList = platforms.filter((p) => p.status === "coming_soon");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain min-h-[92vh] flex items-center">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 25%, rgba(47,107,255,0.16), transparent 45%), radial-gradient(circle at 82% 65%, rgba(124,63,228,0.14), transparent 45%), radial-gradient(circle at 55% 90%, rgba(212,166,60,0.10), transparent 40%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1637775297458-7443ffd545b2?auto=format&fit=crop&w=1800&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Shield emblem */}
        <img
          src="/tib-logo.png"
          alt="TIB shield"
          className="hidden lg:block absolute right-[4%] top-1/2 -translate-y-1/2 w-[420px] xl:w-[480px] object-contain glow-shield opacity-95 pointer-events-none animate-fade-up mix-blend-screen"
        />
        <div className="container-tib relative z-10">
          <div className="max-w-3xl stagger">
            <Overline className="mb-8 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-tib-gold" />
              The build engine for MPGBusinessFunding
            </Overline>
            <h1 className="h-serif text-5xl md:text-7xl lg:text-8xl text-white font-semibold leading-[0.95]">
              Building your dreams,
              <br />
              <span className="text-brand-gradient">one platform at a time.</span>
            </h1>
            <p className="mt-8 text-xl text-zinc-400 max-w-2xl leading-relaxed">
              TechInnovationBuilders is the build engine for MPGBusinessFunding — we turn ideas into
              live, revenue-generating platforms, websites, and apps. We build our own, and we build
              yours, under contract, with ownership handed to you.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/clients/apply" data-testid="hero-cta-build" className="btn-primary">
                Have TIB build your platform <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/platforms" data-testid="hero-cta-portfolio" className="btn-secondary">
                See our portfolio of platforms, websites &amp; apps
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-zinc-900 bg-tib-ink">
        <div className="container-tib grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, i) => (
            <div key={s.label} className={`py-10 px-2 text-center ${i !== 0 ? "border-l border-zinc-900" : ""}`}>
              <div className="h-serif text-4xl md:text-5xl text-tib-amber">{s.value}</div>
              <div className="overline mt-3 text-zinc-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* POSITIONING */}
      <section className="section-pad">
        <div className="container-tib grid lg:grid-cols-2 gap-16 items-start">
          <SectionHeading
            overline="What TIB is"
            title="A build engine with its own economic engine."
            body="Most agencies hand off code and disappear. TIB is a division of the MPGBF holding company — we design, ship, own, and operate the platforms we create. Each build is an asset on the balance sheet, not an invoice."
          />
          <div className="grid gap-4">
            {[
              { icon: Cpu, t: "Design", d: "Architecture, product, and brand — from thesis to blueprint." },
              { icon: Rocket, t: "Ship", d: "Production full-stack builds: React, FastAPI, MongoDB, payments, auth." },
              { icon: Activity, t: "Operate", d: "We run what we build. Uptime, growth, and revenue are ours to keep." },
            ].map((row) => (
              <div key={row.t} className="card-edge p-6 flex gap-5 items-start" data-testid={`positioning-${row.t.toLowerCase()}`}>
                <row.icon className="w-6 h-6 text-tib-amber shrink-0 mt-1" strokeWidth={1.4} />
                <div>
                  <h3 className="text-white font-semibold text-lg">{row.t}</h3>
                  <p className="text-zinc-400 mt-1 leading-relaxed">{row.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad bg-tib-ink border-y border-zinc-900">
        <div className="container-tib">
          <SectionHeading overline="Build models" title="Three ways to build with TIB." className="mb-14" />
          <div className="grid md:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <div key={s.code} className="card-edge p-8" data-testid={`service-${s.code.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <s.icon className="w-7 h-7 text-tib-amber" strokeWidth={1.3} />
                  <span className="font-mono text-xs tracking-widest text-zinc-600">{s.code}</span>
                </div>
                <h3 className="h-serif text-2xl text-white mt-6">{s.title}</h3>
                <p className="text-zinc-400 mt-3 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/services" className="text-tib-amber link-underline inline-flex items-center gap-2" data-testid="services-more">
              Explore the build models <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PLATFORMS */}
      <section className="section-pad">
        <div className="container-tib">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <SectionHeading overline="The portfolio" title="Platforms, shipped and in build." />
            <Link to="/platforms" className="btn-secondary shrink-0" data-testid="platforms-all">All platforms</Link>
          </div>

          {live && (
            <div className="grid md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900 mb-10" data-testid="ideadepot-metric-strip">
              {Object.entries(live.metrics_snapshot || {}).map(([k, v]) => (
                <div key={k} className="bg-black p-6">
                  <div className="h-serif text-3xl text-white">{String(v)}</div>
                  <div className="overline mt-2 text-zinc-500">{k.replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {liveList.map((p) => (
              <PlatformCard key={p.slug} platform={p} />
            ))}
          </div>

          {soonList.length > 0 && (
            <>
              <div className="flex items-center gap-4 mt-16 mb-10">
                <Overline>In build — coming soon</Overline>
                <span className="flex-1 h-px bg-zinc-900" />
                <Link to="/platforms" className="text-xs font-mono text-tib-gold link-underline">view all {soonList.length} →</Link>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {soonList.slice(0, 6).map((p) => (
                  <PlatformCard key={p.slug} platform={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-zinc-900 grain">
        <div className="container-tib section-pad text-center relative z-10">
          <Overline className="mb-6 justify-center flex">Ready to build?</Overline>
          <h2 className="h-serif text-4xl md:text-6xl text-white max-w-3xl mx-auto">
            Bring us a thesis. We'll bring you a platform.
          </h2>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/clients/apply" className="btn-primary" data-testid="cta-apply">
              Apply to build <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="cta-contact">Talk to us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
